import { OrderStatus } from '@thinkalpha/trading-ws-client/contracts/order.js';

/**
 * Helper function to extract order status values from the Zod OrderStatus union type.
 * OrderStatus is a Zod union of literal string types,
 * so we need to extract the actual string values for use in our UI.
 *
 * @returns Array of all available order status string values
 */
export const getOrderStatusValues = (): OrderStatus[] => {
    const statusLiterals = OrderStatus.options?.map((x) => x.value).filter(Boolean) ?? [];
    return statusLiterals as OrderStatus[];
};

let _orderStatusDictionary: Record<OrderStatus, OrderStatus> | undefined;

/**
 * Lazily computes and memoizes a dictionary of order statuses.
 * @returns Record mapping each OrderStatus to itself.
 */
export function getOrderStatusDictionary(): Record<OrderStatus, OrderStatus> {
    if (!_orderStatusDictionary) {
        _orderStatusDictionary = getOrderStatusValues().reduce(
            (acc, status) => {
                acc[status] = status;
                return acc;
            },
            {} as Record<OrderStatus, OrderStatus>,
        );
    }
    return _orderStatusDictionary;
}

export type TableOrderStatus =
    | 'pending new'
    | 'pending cancel'
    | 'canceled'
    | 'filled'
    | 'rejected'
    | 'new'
    | 'partially filled'
    | 'pending replace'
    | 'replaced'
    | 'suspended';

const orderStatusToTableOrderStatusMapping: [OrderStatus, TableOrderStatus | undefined][] = [
    ['created', 'pending new'],
    ['cancel-pending', 'pending cancel'],
    ['canceled', 'canceled'],
    ['filled', 'filled'],
    ['rejected', 'rejected'],
    ['submitted', 'new'],
    ['partially-filled', 'partially filled'],
    ['update-pending', 'pending replace'],
    // trading-ws does not use a 'replaced' status, but the Orders table does show it, we map it to a closed status
    ['canceled', 'replaced'],
    ['suspended', 'suspended'],
    // TODO: What do these show in the Orders table when they exist?
    ['pending', undefined],
    ['kill-pending', undefined],
    ['accepted', undefined],
];

// Smart constructor function to ensure type safety when using order statuses
export function orderStatus(status: OrderStatus): OrderStatus {
    return status;
}

export function tableOrderStatusToOrderStatus(tableOrderStatus?: string): OrderStatus | undefined {
    if (!tableOrderStatus) {
        return undefined;
    }

    const normalized = tableOrderStatus.toLowerCase().replace(/\s+/g, ' ').trim();

    const [orderStatus] = orderStatusToTableOrderStatusMapping.find(
        ([, tableStatus]) => tableStatus === normalized,
    ) || [undefined];

    return orderStatus;
}

export const OPEN_ORDERS: OrderStatus[] = [
    orderStatus('created'),
    orderStatus('pending'),
    orderStatus('partially-filled'),
    orderStatus('submitted'),
    orderStatus('suspended'),
    orderStatus('updated'),
    orderStatus('update-pending'),
    orderStatus('cancel-pending'),
    orderStatus('accepted'),
    orderStatus('kill-pending'),
];
